import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import {
    CsvAsStringArrays,
    CsvInsightChart,
    CsvRestaurantInsightsChart,
    InsightsTab,
    MalouComparisonPeriod,
    PlatformKey,
} from '@malou-io/package-utils';

import { UserStore } from '#core/store/models/user-store.model';
import { Restaurant } from '#modules/restaurants/models/restaurant';
import { DownloadInsightsCsvSummaryBaseService } from '#modules/statistics/services/csv-insights-summary/download-insights-summary-base.service';
import { CsvInsightsSummaryService } from '#modules/statistics/services/csv-summary/insights/summary.service';
import { StatisticsFiltersStore } from '#modules/statistics/stores/store/statistics-filters-store/models/statistics-filters-store.model';
import { CsvStringCreator } from '#shared/helpers/create-csv-string';

interface CsvInsightsSummaryParams {
    csvChart: CsvRestaurantInsightsChart;
    options: {
        startDate: Date;
        endDate: Date;
        comparisonPeriod: MalouComparisonPeriod;
        platformKeys: PlatformKey[];
        restaurant: Restaurant;
    };
}

@Injectable()
export class DownloadInsightsSummaryService extends DownloadInsightsCsvSummaryBaseService {
    private readonly _csvStringCreator = inject(CsvStringCreator);
    private readonly _csvInsightsSummaryService = inject(CsvInsightsSummaryService);

    downloadCsvRestaurantInsightsSummary({
        insightsTab,
        csvChart,
        currentUser,
        platformKeys,
        statisticsFilters,
        restaurant,
    }: {
        insightsTab: InsightsTab;
        csvChart: CsvRestaurantInsightsChart;
        currentUser: UserStore;
        platformKeys: PlatformKey[];
        statisticsFilters: StatisticsFiltersStore;
        restaurant: Restaurant;
    }): void {
        const footerId = this.showFooter();
        if (!statisticsFilters.dates || !statisticsFilters.comparisonPeriod) {
            this.hideFooter(footerId);
            return;
        }
        const { startDate, endDate } = statisticsFilters.dates;
        const comparisonPeriod = statisticsFilters.comparisonPeriod;
        this.getCsvRestaurantInsightsSummaryData$({
            csvChart,
            options: {
                startDate: startDate!,
                endDate: endDate!,
                comparisonPeriod,
                platformKeys,
                restaurant,
            },
        })
            .pipe(
                map((csvStringData) => {
                    if (!csvStringData || !csvStringData.length) {
                        return null;
                    }
                    this.generatedCsvFiles({
                        csvStringData,
                        footerId,
                        insightsTab,
                        csvChart,
                        startDate,
                        endDate,
                        currentUser,
                    });
                }),
                catchError((err) => {
                    console.error('Error downloading insights summary:', err);
                    this.hideFooter(footerId);
                    this.toastService.openErrorToast(this.translateService.instant('common.unknown_error'));
                    return [];
                })
            )
            .subscribe();
    }

    getCsvRestaurantInsightsSummaryData$({ csvChart, options }: CsvInsightsSummaryParams): Observable<string | null> {
        let csvData$: Observable<CsvAsStringArrays | null>;
        switch (csvChart) {
            case CsvInsightChart.INSIGHTS_SUMMARY:
                csvData$ = this._csvInsightsSummaryService.getCsvData$(options);
                break;
            default:
                return of(null);
        }

        return csvData$.pipe(
            map((csvData) => {
                if (!csvData) {
                    return null;
                }
                return this._csvStringCreator.create(csvData);
            })
        );
    }
}
